/**
 * The one top row of a Scores detail screen (game, team, standings): a 44px
 * round back button with a chevron, the screen's own title as its `h1`, and
 * the screen's actions at the right (Scores UX spec v1.0 sections 7.1, 8 and
 * 10). It replaces the stacked "Back to Scores" text link plus the generic
 * "Scores" h1, so a phone spends one 44px row on navigation instead of two.
 *
 * The back button is a link into `/scores` that clears the keys this screen
 * owns and keeps the rest of the search, so Back lands on the same league
 * and day. Its accessible name is the screen's back label. A screen whose
 * name is its header (the team page) passes no title, and its header carries
 * the `h1`.
 */
import { cn } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { ICON_BUTTON_CLASS } from "../../icons/actionIcons";
import { ChevronLeftIcon } from "../../icons/glyphs";

export type typeDetailNavBarProps = {
  backLabel: string;
  /** The search keys Back clears, such as `eventId` and `tab`. */
  clearKeys: readonly string[];
  title?: string | undefined;
  /** The trailing actions, each a 44px target. */
  actions?: ReactNode;
  testId?: string | undefined;
};

export const DetailNavBar = (props: typeDetailNavBarProps): ReactNode => (
  <div
    className="flex min-h-touch-target min-w-0 items-center gap-2"
    data-testid={props.testId ?? "detail-nav"}
  >
    <Link
      to="/scores"
      search={(prev: Record<string, unknown>) => {
        const next: Record<string, unknown> = { ...prev };
        for (const key of props.clearKeys) next[key] = undefined;
        return next;
      }}
      aria-label={props.backLabel}
      data-testid="detail-nav-back"
      className={cn(ICON_BUTTON_CLASS, "text-primary no-underline")}
    >
      <ChevronLeftIcon size="md" />
    </Link>
    {props.title !== undefined ? (
      <h1
        className="min-w-0 flex-1 text-h2 text-ink"
        data-testid="detail-nav-title"
      >
        {props.title}
      </h1>
    ) : (
      <span className="flex-1" />
    )}
    {props.actions !== undefined ? (
      <div
        className="flex shrink-0 items-center"
        data-testid="detail-nav-actions"
      >
        {props.actions}
      </div>
    ) : null}
  </div>
);
